import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/**
 * Initializes global scroll animations based on data attributes.
 * Also sets up a ResizeObserver to auto-refresh GSAP when the DOM height changes.
 */
export const initScrollAnimations = (): void => {
  // 1. Initialize data-gsap triggers
  initDataGsapElements();

  // 2. Start the global layout observer
  initGlobalLayoutObserver();
};

const initDataGsapElements = () => {
  const elements = document.querySelectorAll<HTMLElement>("[data-gsap]");

  if (elements.length === 0) return;

  elements.forEach((element) => {
    const animationType = element.dataset.gsap;
    const delay = parseFloat(element.dataset.gsapDelay || "0");
    const duration = parseFloat(element.dataset.gsapDuration || "0.8");

    const triggerSettings = {
      trigger: element,
      start: "top 95%",
      toggleActions: "play none none reverse",
    };

    switch (animationType) {
      case "slide-up":
        gsap.fromTo(
          element,
          { autoAlpha: 0, y: 50 },
          {
            autoAlpha: 1,
            y: 0,
            duration,
            delay,
            ease: "power2.out",
            scrollTrigger: triggerSettings,
          }
        );
        break;
      case "slide-left":
        gsap.fromTo(
          element,
          { autoAlpha: 0, x: 50 },
          {
            autoAlpha: 1,
            x: 0,
            duration,
            delay,
            ease: "power2.out",
            scrollTrigger: triggerSettings,
          }
        );
        break;
      case "slide-right":
        gsap.fromTo(
          element,
          { autoAlpha: 0, x: -50 },
          {
            autoAlpha: 1,
            x: 0,
            duration,
            delay,
            ease: "power2.out",
            scrollTrigger: triggerSettings,
          }
        );
        break;
      case "fade":
        gsap.fromTo(
          element,
          { autoAlpha: 0 },
          {
            autoAlpha: 1,
            duration,
            delay,
            ease: "power2.out",
            scrollTrigger: triggerSettings,
          }
        );
        break;
      case "zoom-in":
        gsap.fromTo(
          element,
          { autoAlpha: 0, scale: 0.95 },
          {
            autoAlpha: 1,
            scale: 1,
            duration,
            delay,
            ease: "power2.out",
            scrollTrigger: triggerSettings,
          }
        );
        break;
      default:
        gsap.fromTo(
          element,
          { autoAlpha: 0 },
          {
            autoAlpha: 1,
            duration,
            delay,
            ease: "power2.out",
            scrollTrigger: triggerSettings,
          }
        );
    }
  });

  console.log(`[GSAP] Initialized ${elements.length} scroll triggers.`);
};

/**
 * Automates GSAP Refresh.
 * Watches the document body for height changes (e.g., loaded images, react components expanding/shrinking).
 */
const initGlobalLayoutObserver = () => {
  let timer: number | undefined;

  const observer = new ResizeObserver(() => {
    // Clear any pending refresh
    if (timer) clearTimeout(timer);

    // Debounce: Wait 300ms after the last size change before refreshing.
    // This prevents performance lag if the page is resizing continuously (e.g. dragging window).
    timer = window.setTimeout(() => {
      ScrollTrigger.refresh();
      // console.log("[GSAP] Auto-refreshed ScrollTrigger due to layout change.");
    }, 300);
  });

  // Watch the body for height changes
  observer.observe(document.body);
};

// We keep this export in case you ever need to force it manually,
// but the observer above usually makes it unnecessary.
export const refreshScrollTriggers = (): void => {
  ScrollTrigger.refresh();
};
